import { register, unregister } from '../serviceWorkerRegistration'
import { STATIC_CACHE_NAME } from '../service-worker'
import { MESSAGE_PRECACHE } from '../service-worker'

const CACHE_STORE_KEY = 'vn-app-cache'

/**
 * Clears caches in the service worker.
 *
 * @returns {Promise<string[]>} resolves to an array of cache names
 */
export const clearCaches = async () => {
  const names = await caches.keys()
  await names.forEach(async (cacheName) => {
    await caches.delete(cacheName)
  })
  return names
}

/**
 * Send Message to Service Worker
 *
 * @param {string} type
 * @param {any} payload
 * @returns
 */
export const sendMessage = async (type, payload = null) => {
  if ('serviceWorker' in navigator === false) {
    return
  }
  const registration = await navigator.serviceWorker.ready

  return await registration.active.postMessage({ type, payload })
}

/**
 * Invalidates all caches and re-fetches precache list
 */
export const invalidateCaches = async () => {
  try {
    await clearCaches()
    await sendMessage(MESSAGE_PRECACHE)
  } catch (e) {
    console.error('Error invalidading caches:', e)
  }
}

/**
 * Lists cache content by cache name
 *
 * @param {string} cacheName
 * @returns Collection of entries of "cache - routes"
 */
const getCacheContentsListByName = async (cacheName) => {
  const cacheList = []
  const cache = await caches.open(cacheName)
  const cachedRequests = await cache.keys()

  cachedRequests.forEach((request) => {
    cacheList.push(`${cacheName} - ${request.url}`)
  })

  return cacheList
}

/**
 * Lists all avaiable caches and routes
 *
 * @returns Collection of entries of "cache - routes"
 */
export const getCacheContentsList = async (keyName = '') => {
  const cacheList = []
  const cacheNames = await caches.keys() // Get all cache names

  for (const cacheName of cacheNames) {
    if (keyName.length && !cacheName.includes(keyName)) {
      continue
    }

    const curList = await getCacheContentsListByName(cacheName)
    cacheList.push(...curList)
  }

  return cacheList
}

/**
 * Checks for successfuly pre-cache of workbox static files
 *
 * @returns {boolean}
 */
export const isPreCacheSuccessful = async () => {
  const cacheList = await getCacheContentsList(STATIC_CACHE_NAME)

  return !!cacheList.length
}

/**
 * Clears a specific cache by its name.
 *
 * @param {string} cacheName
 * @returns {Promise<boolean>} Resolves to a boolean indicating whether the cache was successfully deleted.
 */
export const clearCacheByName = async (cacheName) => {
  return await caches.delete(cacheName)
}

export const isCacheEnabled = () => {
  if (!localStorage) return
  return +localStorage.getItem(CACHE_STORE_KEY) === 1
}

/**
 * Using Service workers
 * Learn more about service workers: https://bit.ly/CRA-PWA
 *
 */
export const enableCache = () => {
  try {
    register({ forceRegistration: true, primeCache: true })
    localStorage.setItem(CACHE_STORE_KEY, 1)
  } catch (err) {
    localStorage.setItem(CACHE_STORE_KEY, 0)
    console.error('SW enableCache:', err)
    throw new Error('Error initializing Application Cache')
  }
}

/**
 * Clear all caches, unregisters service worker and reloads browser
 * Must be online
 */
export const disableCache = () => {
  try {
    unregister()
    localStorage.setItem(CACHE_STORE_KEY, 0)
  } catch (err) {
    console.error('SW disableCache:', err)
    throw new Error('Error unregistering Application Cache')
  }
}
